<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { NumberFormat } from "vue-i18n";
import { pluralize } from "common/utils/format/word";
import { n } from "components/jiffy/vue-cart/helpers/currency";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { trackPDPPageVisit } from "components/pod/pod-build-order/utils/ga_tracker";
import {
  getDefaultVariant,
  getBulkPrice,
  calculateProductTotalPrice,
  compositionPrice
} from "components/pod/pod-build-order/utils/pricing";
import ProductQuality from "./ProductQuality.vue";
import { trackPrintReviewsClick, trackProductClick } from "../utils/tracker";

import SoftnessScore from "./SoftnessScore.vue";

const TILE_WIDTH = 160;
const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  position: {
    type: Number,
    required: true
  },
  sectionContext: {
    type: String,
    required: true
  }
});
const store = useStore();
const compositionRef = ref(null);

const selectedProduct = computed(() => store.getters["product/selectedProduct"]);
const isCompositionSelected = computed(() => props.product.id === selectedProduct.value?.id);
const totalQty = computed(() => store.getters["product/totalQty"]);

onMounted(() => {
  const observer = new IntersectionObserver(
    entries => {
      if (!isCompositionSelected.value) return;
      const { intersectionRect } = entries[0];
      if (intersectionRect.width && intersectionRect.width <= TILE_WIDTH / 2) {
        const { product } = props;
        store.commit("product/setSelectedProduct", null);

        trackPDPPageVisit(product, currentComposition.value, pricePerPiece.value);
      }
    },
    { threshold: 0.5 }
  );

  observer.observe(compositionRef.value);
});

const productsSelectedComposition = computed(
  () => store.getters["product/productsSelectedComposition"]
);
const deliveryDate = computed(() => store.getters["delivery/deliveryDate"]);
const isDeliveryLoading = computed(() => store.getters["loading/delivery"]);

const defaultComposition = computed(() => props.product.compositions.find(comp => comp.default));
const currentComposition = computed(() => {
  const selectedCompId = productsSelectedComposition.value[props.product.id];
  if (selectedCompId) {
    return props.product.compositions.find(comp => comp.id === selectedCompId);
  }
  return defaultComposition.value;
});

const prices = computed(() => store.getters["product/prices"]);
const totalsNoDiscount = computed(() => store.getters["product/totalsNoDiscount"]);
const assumptiveQuantity = computed(() => store.getters["product/assumptiveQuantity"]);
const priceLabel = computed(() => pluralize(assumptiveQuantity.value, "piece"));
const calculateBulkPrice = (variantPrices, withAssumptive) =>
  getBulkPrice({
    variantPrices,
    totalsNoDiscount: totalsNoDiscount.value,
    assumptiveQuantity: withAssumptive ? assumptiveQuantity.value : 0
  });

const variantQuantities = computed(() => store.getters["product/variantQuantities"]);

const productTotalPrice = computed(() =>
  calculateProductTotalPrice({
    product: props.product,
    prices: prices.value,
    variantQuantities: variantQuantities.value,
    totalsNoDiscount: totalsNoDiscount.value
  })
);
const calculateCompositionPrice = composition =>
  compositionPrice({
    composition,
    prices: prices.value,
    productTotalPrice: productTotalPrice.value,
    totalsNoDiscount: totalsNoDiscount.value,
    assumptiveQuantity: assumptiveQuantity.value
  });

const compositionOldPrice = computed(() => {
  const { id } = currentComposition.value;
  let compPrices = prices.value[id];

  if (!compPrices) {
    compPrices = prices.value[defaultComposition.value.id];
  }

  return compPrices?.presalePrice || null;
});

const currentCompositionPrice = computed(() => {
  const price = calculateCompositionPrice(currentComposition.value);
  if (price) return price;
  return calculateCompositionPrice(defaultComposition.value) || 0;
});

const pricePerPiece = computed(() => currentCompositionPrice.value);

const productImage = computed(() => currentComposition.value.imageUrl);

const assumptiveProductTotalPrice = computed(() => {
  const { id, variants } = defaultComposition.value;
  const defaultVariant = getDefaultVariant(variants);
  const compPrices = prices.value[id];

  if (!compPrices) return 0;
  return calculateBulkPrice(compPrices[defaultVariant.id], true) * assumptiveQuantity.value;
});
const productCompositionsCount = computed(() =>
  store.getters["product/productsCompositionsCount"](props.product.id)
);

const freeShippingThreshold = computed(() => store.getters["checkout/freeShippingThreshold"]);
const isFreeShipping = computed(() => totalsNoDiscount.value - freeShippingThreshold.value > 0);

const handleProductSelect = () => {
  const { product, sectionContext, position } = props;

  store.dispatch("product/handleProductSelect", product);

  trackPDPPageVisit(product, currentComposition.value, pricePerPiece.value);
  trackProductClick(product, currentComposition.value, sectionContext, position);
};

const handleReviewClick = () => {
  trackPrintReviewsClick(props.product);
};
</script>

<template>
  <div class="product-composition">
    <a
      ref="compositionRef"
      class="product-composition__wrapper"
      :class="{
        'product-composition__wrapper--selected js-selected-composition-scroll-target':
          isCompositionSelected
      }"
      @click="handleProductSelect"
    >
      <div v-if="productCompositionsCount" class="product-composition__product-count-wrapper">
        <UseSvg icon="shopping-cart" type="print" class="product-composition__cart-icon" />
        <div class="product-composition__product-count">
          {{ productCompositionsCount }}
        </div>
      </div>
      <div v-if="product.quality" class="product-composition__quality-wrapper">
        <ProductQuality :quality="product.quality" />
      </div>
      <div class="product-composition__image-wrapper">
        <img class="product-composition__image" :src="productImage" :alt="product.name" />
        <div
          class="product-composition__price"
          :class="{ 'product-composition__price--with-sale': compositionOldPrice }"
        >
          <div v-if="compositionOldPrice" class="product-composition__old-price">
            {{ n(compositionOldPrice, "currency") }}
          </div>
          <NumberFormat
            v-if="pricePerPiece"
            tag="div"
            class="product-composition__sale-price"
            :value="pricePerPiece"
            format="currency"
            scope="global"
          >
            <template #currency="slotProps">
              <span>{{ slotProps.currency }}</span>
            </template>
            <template #integer="slotProps">
              <span class="product-composition__price-integer">{{ slotProps.integer }}</span>
            </template>
          </NumberFormat>
        </div>
        <div v-if="props.product.cozyfeelScore" class="product-composition__softness-score">
          <SoftnessScore :value="product.cozyfeelScore" />
        </div>
      </div>
      <div class="product-composition__deal">
        <div class="product-composition__deal-badge">
          <UseSvg
            icon="limited-deal-dollar"
            type="print"
            width="13"
            height="13"
            class="product-composition__deal-icon"
          />
          Holiday Sale Price
        </div>
      </div>
      <div class="product-composition__info">
        <div class="product-composition__brand">{{ product.brandAndCatalogNumber }}</div>
        <div class="product-composition__name">{{ product.name }}</div>
      </div>
      <div class="product-composition__rating" @click="handleReviewClick">
        <UseSvg icon="star-fill" class="product-composition__rating-icon" />
        {{ product.averageRating }} ({{ product.reviewsCount }})
      </div>
      <div v-if="productTotalPrice" class="product-composition__totals">
        <span class="product-composition__total-price">{{ n(productTotalPrice, "currency") }}</span>
        <span class="product-composition__total-price-label">total</span>
      </div>
      <div v-else-if="totalQty" class="product-composition__totals">
        <span class="product-composition__total-price">{{ n(pricePerPiece, "currency") }}</span>
        <span class="product-composition__total-price-label"> each</span>
      </div>
      <div v-else-if="assumptiveProductTotalPrice" class="product-composition__totals">
        <span class="product-composition__total-price">{{
          n(assumptiveProductTotalPrice, "currency")
        }}</span>
        <span class="product-composition__total-price-label"> for {{ priceLabel }} </span>
      </div>

      <div class="product-composition__shipping">
        <div v-if="isFreeShipping">Free Delivery</div>
        <div
          v-if="deliveryDate.full && !isDeliveryLoading"
          class="product-composition__delivery-date"
        >
          {{ deliveryDate.full }}
        </div>
        <div
          v-else-if="isDeliveryLoading"
          class="product-composition__delivery-date product-composition__delivery-date--loading"
        ></div>
      </div>
    </a>
    <div v-if="isCompositionSelected" class="product-composition__selected-delimiter" />
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

$badge-color: #0ca045;

.product-composition {
  flex: 0 0 160px;
  position: relative;

  &__wrapper {
    border: $composition-border-width solid $digital-assets-gray-modern-200;
    border-radius: 6px;
    height: 371px;
    width: 160px;
    position: relative;
    z-index: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: $digital-assets-gray-modern-600;
    padding: 8px;

    &:focus,
    &:active,
    &:visited {
      text-decoration: none;
      color: $digital-assets-gray-modern-600;
    }

    &--selected {
      border-color: $link-blue;
      background: $pod-gray-modern-100;
      border-bottom: 0;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  &__quality-wrapper {
    position: absolute;
    top: -10px;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  &__info {
    padding: 2px 0 9px;
    width: 100%;
    border-bottom: 1px solid $pod-gray-modern-200;
  }

  &__brand {
    color: $pod-gray-modern-500;
    font-size: 10px;
    font-weight: 500;
  }

  &__name {
    font-size: 11px;
    font-weight: 500;
    color: $pod-gray-modern-900;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 160px;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__image-wrapper {
    width: 132px;
    height: 198px;
    border-radius: 4px;
    position: relative;
    flex-shrink: 0;
  }

  &__rating {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 400;
    margin-top: 8px;
    width: 100%;
  }

  &__rating-icon {
    color: $accent-grapefruit;
    width: 13px;
    height: 13px;
  }

  &__softness-score {
    width: 65px;
    height: 30px;
    position: absolute;
    background: $spindle-light;
    border-radius: 4px;
    right: 0;
    bottom: 6px;
    padding-top: 3px;
  }

  &__price {
    width: 61px;
    height: 30px;
    position: absolute;
    background: $red-light;
    color: $white;
    border-radius: 4px;
    left: 0;
    bottom: 6px;
    padding: 7px 12px 5px 13px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1;

    &--with-sale {
      padding: 3px 12px 0 13px;
      text-align: center;
    }
  }

  &__sale-price {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    gap: 1.5px;
  }

  &__old-price {
    font-size: 10px;
    font-weight: 600;
    line-height: 100%;
    text-decoration: line-through;
    opacity: 0.7;
  }

  &__price-integer {
    font-size: 17px;
    line-height: 15px;
  }

  &__price-group {
    display: none;
  }

  &__price-per-piece {
    font-size: 17px;
    font-weight: 600;
    color: $red-light;
  }

  &__totals {
    width: 100%;
    line-height: 15px;
  }

  &__total-price {
    font-size: 12px;
    font-weight: 400;
    margin-right: 2px;
  }

  &__total-price-label {
    font-size: 10px;
    color: $pod-gray-modern-500;
  }

  &__shipping {
    margin-top: 8px;
    font-size: 10px;
    font-weight: 400;
    line-height: 16px;
    width: 100%;
  }

  &__delivery-date {
    color: $pod-date-orange;
    font-size: 13px;
    font-weight: 600;
    line-height: 120%;
    min-height: 16px;

    &--loading {
      @include vlc-animation;

      position: relative;
      border-radius: 6px;
      background-color: $pod-gray-modern-100;
    }
  }

  &__selected-delimiter {
    position: absolute;
    height: 20px;
    bottom: -20px;
    left: 0;
    right: 0;
    border-left: $composition-border-width solid $blue;
    border-right: $composition-border-width solid $blue;
    background: $pod-gray-modern-100;
    z-index: 10;
  }

  &__product-count-wrapper {
    position: absolute;
    width: 34px;
    height: 34px;
    color: $white;
    background: $pod-gray-modern-900;
    border-radius: 50%;
    right: 18px;
    top: 18px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__cart-icon {
    width: 16px;
    height: 16px;
  }

  &__product-count {
    position: absolute;
    top: 0;
    right: -2px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    background: $medium-slate-blue;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 600;
    color: $white;
    text-align: center;
    border: 1px solid $white;
  }

  &__deal {
    width: 100%;
    margin-top: 6px;
    padding: 0 4px;
  }

  &__deal-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 4px;
    background: $badge-color;
    color: $white;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.1;
    width: 100%;
  }

  &__deal-icon {
    flex-shrink: 0;
    width: 13px;
    height: 13px;
  }
}
</style>
